import { useEffect, useState, useCallback, useRef } from "react";
import { Wind } from "@phosphor-icons/react";

/**
 * Compact weather pill for the Round screen. Shows live wind direction + speed
 * relative to the shot you're playing (head/tail/cross-wind label when pin
 * coords are available).
 *
 * Free data source: https://open-meteo.com (no API key, CORS enabled).
 * Refreshes every 15 minutes and only re-fetches when the player has moved
 * more than ~250m from the last fetch position (saves bandwidth on long rounds).
 */
const REFRESH_MS = 15 * 60 * 1000;
const MIN_DIST_TO_REFETCH_M = 250;
const STALE_MS = 60 * 60 * 1000; // 1h — show "stale" hint after this

function bearing(lat1, lng1, lat2, lng2) {
  // Initial bearing in degrees (0=N, 90=E)
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function meters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function relativeLabel(windFromDeg, shotBearing) {
  if (windFromDeg == null || shotBearing == null) return null;
  // Wind comes FROM `windFromDeg`. Express that relative to where the player is aiming.
  const rel = ((windFromDeg - shotBearing + 540) % 360) - 180; // -180..180
  const abs = Math.abs(rel);
  if (abs <= 22.5) return { code: "head", text: "Headwind" };
  if (abs >= 157.5) return { code: "tail", text: "Tailwind" };
  if (abs <= 67.5) return rel > 0 ? { code: "cross_rl", text: "Cross R→L" } : { code: "cross_lr", text: "Cross L→R" };
  if (abs <= 112.5) return rel > 0 ? { code: "cross_rl", text: "Cross R→L" } : { code: "cross_lr", text: "Cross L→R" };
  return rel > 0 ? { code: "tail_r", text: "Tailwind R" } : { code: "tail_l", text: "Tailwind L" };
}

export default function WeatherPill({ playerLat, playerLng, pinLat, pinLng, onWeather }) {
  const [weather, setWeather] = useState(null);
  const [err, setErr] = useState("");
  const lastFetchRef = useRef({ lat: null, lng: null, at: 0 });

  const fetchWeather = useCallback(async (lat, lng) => {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 8000);
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,wind_speed_10m,wind_direction_10m,wind_gusts_10m,weather_code&wind_speed_unit=mph&temperature_unit=celsius&timezone=auto`;
      const res = await fetch(url, { signal: ctrl.signal });
      clearTimeout(t);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const c = data?.current || {};
      setWeather({
        tempC: typeof c.temperature_2m === "number" ? Math.round(c.temperature_2m) : null,
        windMph: typeof c.wind_speed_10m === "number" ? Math.round(c.wind_speed_10m) : null,
        windFromDeg: typeof c.wind_direction_10m === "number" ? c.wind_direction_10m : null,
        gustMph: typeof c.wind_gusts_10m === "number" ? Math.round(c.wind_gusts_10m) : null,
        weatherCode: c.weather_code ?? null,
        fetchedAt: Date.now(),
      });
      setErr("");
      lastFetchRef.current = { lat, lng, at: Date.now() };
    } catch (e) {
      if (e.name !== "AbortError") setErr("offline");
    }
  }, []);

  // Decide when to (re)fetch
  useEffect(() => {
    if (playerLat == null || playerLng == null) return;
    const last = lastFetchRef.current;
    const stale = Date.now() - last.at > REFRESH_MS;
    const moved =
      last.lat == null ||
      meters(last.lat, last.lng, playerLat, playerLng) > MIN_DIST_TO_REFETCH_M;
    if (!weather || stale || moved) {
      fetchWeather(playerLat, playerLng);
    }
    const id = setInterval(() => {
      fetchWeather(playerLat, playerLng);
    }, REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerLat, playerLng]);

  // Bubble weather state up to parent (for the club suggestion chip).
  // We expose mph + windRelativeDeg = (windFromDeg - shotBearing + 540) % 360 - 180,
  // but flipped so 0=head, 180=tail (which matches our adjustDistanceForWind helper).
  useEffect(() => {
    if (!onWeather) return;
    if (!weather || weather.windMph == null) {
      onWeather(null);
      return;
    }
    const shotBrng =
      pinLat != null && pinLng != null
        ? bearing(playerLat, playerLng, pinLat, pinLng)
        : null;
    const relativeDeg =
      shotBrng != null && weather.windFromDeg != null
        ? ((weather.windFromDeg - shotBrng + 360) % 360)
        : null;
    onWeather({
      mph: weather.windMph,
      gustMph: weather.gustMph,
      tempC: weather.tempC,
      windFromDeg: weather.windFromDeg,
      relativeDeg,
    });
  }, [weather, playerLat, playerLng, pinLat, pinLng, onWeather]);

  if (playerLat == null || playerLng == null) {
    return (
      <span
        data-testid="weather-pill-empty"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-white/10 text-white/60"
      >
        <Wind size={11} weight="bold" /> Wind
      </span>
    );
  }
  if (err && (!weather || weather.windMph == null)) {
    return (
      <span
        data-testid="weather-pill-error"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-white/10 text-white/60"
        title="Weather unavailable — check connection"
      >
        <Wind size={11} weight="bold" /> N/A
      </span>
    );
  }
  if (!weather) {
    return (
      <span
        data-testid="weather-pill-loading"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-white/10 text-white/70"
      >
        <Wind size={11} weight="bold" /> …
      </span>
    );
  }

  const shotBearing =
    pinLat != null && pinLng != null
      ? bearing(playerLat, playerLng, pinLat, pinLng)
      : null;
  const rel = relativeLabel(weather.windFromDeg, shotBearing);
  // Arrow points in the direction wind is BLOWING TO (= from + 180)
  const arrowRotation = (weather.windFromDeg ?? 0) + 180;

  const isStale = Date.now() - weather.fetchedAt > STALE_MS;
  // Visual tone by wind strength
  const tone =
    weather.windMph >= 15 ? "strong" : weather.windMph >= 8 ? "medium" : "calm";
  const toneClass = {
    calm: "bg-white/10 text-white",
    medium: "bg-amber-300/25 text-amber-50",
    strong: "bg-red-400/30 text-white",
  }[tone];

  return (
    <span
      data-testid="weather-pill"
      title={
        rel
          ? `${rel.text} · ${weather.windMph} mph` +
            (weather.gustMph ? ` (gust ${weather.gustMph})` : "") +
            (weather.tempC != null ? ` · ${weather.tempC}°C` : "")
          : `${weather.windMph} mph` +
            (weather.tempC != null ? ` · ${weather.tempC}°C` : "")
      }
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 ${toneClass} ${
        isStale ? "opacity-60" : ""
      }`}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        style={{ transform: `rotate(${arrowRotation}deg)`, transition: "transform 0.6s ease" }}
        aria-hidden="true"
        data-testid="weather-pill-arrow"
      >
        <path
          d="M12 2 L18 14 L12 11 L6 14 Z"
          fill="currentColor"
        />
      </svg>
      <span className="text-[10px] uppercase tracking-wider font-bold font-mono-num" data-testid="weather-pill-mph">
        {weather.windMph}
      </span>
      <span className="text-[9px] uppercase tracking-wider opacity-80 font-bold">mph</span>
      {rel && (
        <span
          className="text-[9px] uppercase tracking-wider font-bold opacity-90 border-l border-white/30 pl-1.5 ml-0.5"
          data-testid="weather-pill-relative"
        >
          {rel.code === "head" && "Head"}
          {rel.code === "tail" && "Tail"}
          {rel.code === "cross_rl" && "R→L"}
          {rel.code === "cross_lr" && "L→R"}
          {rel.code === "tail_r" && "Tail R"}
          {rel.code === "tail_l" && "Tail L"}
        </span>
      )}
    </span>
  );
}
